import { Injectable } from '@angular/core';

/**
 * Mantém o "contexto de acesso" (tela de origem) atual, declarado ao backend
 * via header X-Access-Context.
 *
 * Modelo "Direct vs Indirect Access":
 *   - Quando uma tela que o perfil TEM direito (ex: Dashboard) dispara um
 *     endpoint compartilhado com uma tela NEGADA (ex: aba Consolidação),
 *     ela declara seu próprio screen_key como contexto. O backend então
 *     libera o endpoint (override) porque o usuário tem acesso àquele contexto.
 *   - A decisão de permissão fica 100% no backend (CheckScreenPermission +
 *     ResolveAccessContextAction). O frontend apenas declara o contexto.
 *
 * Uso típico em um componente de tela:
 *   ngOnInit(): void {
 *       this._accessContext.set('dash-target-balance');
 *       ...
 *   }
 *   ngOnDestroy(): void {
 *       this._accessContext.clear();
 *   }
 *
 * Caso não seja chamado set()/clear(), o comportamento é o mesmo de antes
 * (sem contexto = acesso direto governado pela screen_key primária do endpoint).
 */
@Injectable({ providedIn: 'root' })
export class AccessContextService
{
    private _currentContext: string | null = null;

    /**
     * Define o contexto de acesso atual (a screen_key da tela ativa).
     * Será enviado como header X-Access-Context em todas as requests HTTP.
     */
    set(screenKey: string): void
    {
        this._currentContext = screenKey || null;
    }

    /**
     * Limpa o contexto (volta ao comportamento de acesso direto, sem override).
     */
    clear(): void
    {
        this._currentContext = null;
    }

    /**
     * Retorna o contexto atual, ou null se nenhum estiver definido.
     */
    current(): string | null
    {
        return this._currentContext;
    }
}
